import { useEffect, useState } from 'react';
import {
  Box, Paper, Grid, Typography, Button, LinearProgress, Chip,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  pageVariants, sectionVariants,
} from '../../../animations/variants';
import { getComplianceSummary } from '../../../services/securityhubApi';
import { standardLabel } from './standards';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalEmptyState, PortalErrorState,
} from '../../../components/portal/PortalStates';
import {
  CK, FONT_BODY, statusChipSx, sectionCardSx, statTileSx, statTileFooterSx, tileEntranceStyle
} from '../portalStyles';

const barColor = (pct) => (pct >= 80 ? 'success' : pct >= 50 ? 'warning' : 'error');
const scoreSx = (pct) => ({
  fontWeight: 700,
  color: pct >= 80 ? 'success.main' : pct >= 50 ? 'warning.main' : 'error.main',
});

// Dedicated "Security standards" page — a card per enabled standard with its
// security score, mirroring the AWS Security Hub standards page. Each card
// drills into the standard's controls (StandardDetail). Distinct from the
// Summary, which is the severity + cross-standard overview.
export default function StandardsList() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getComplianceSummary()
      .then((res) => setSummary(res?.data || {}))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const standards = summary?.perStandard || [];

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
        <PortalPageHeader
          icon={<SecurityIcon sx={{ fontSize: 24 }} />}
          title="Security standards"
          subtitle="Compliance standards evaluated by Security Hub CSPM"
          action={
            <Button variant="contained" startIcon={<SettingsIcon />} onClick={() => navigate('/securityhub/cspm/settings')}>
              Manage standards
            </Button>
          }
        />
      </motion.div>

      {error && (
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} />
        </motion.div>
      )}

      {loading && (
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading security standards…" />
          </Paper>
        </motion.div>
      )}

      {!loading && !error && standards.length === 0 && (
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalEmptyState
              icon={<SecurityIcon sx={{ fontSize: 28 }} />}
              title="No standards enabled"
              description="Use “Manage standards” to enable a compliance standard and start scoring your accounts."
              action={(
                <Button variant="contained" startIcon={<SettingsIcon />} onClick={() => navigate('/securityhub/cspm/settings')}>
                  Manage standards
                </Button>
              )}
            />
          </Paper>
        </motion.div>
      )}

      {standards.length > 0 && (
        <motion.div variants={sectionVariants}>
          {/* No motion wrapper: the tiles animate themselves in pure CSS, and a
              motion component here can write an inline opacity onto them once
              its own animation settles. */}
            <Grid container spacing={3}>
              {standards.map((std, i) => {
                const total = std.evaluatedCount ?? (std.passingCount + std.failingCount);
                const pct = std.passingPercent || 0;
                const noData = total === 0;
                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={std.standardId}>
                    <div style={tileEntranceStyle(i)}>
                      <Paper
                        elevation={0}
                        sx={{
                          ...statTileSx,
                          cursor: 'pointer',
                          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                          '&:hover': { borderColor: CK.blue, boxShadow: `0 4px 12px ${CK.blue}20` },
                        }}
                        onClick={() => navigate(`/securityhub/cspm/standards/${encodeURIComponent(std.standardId)}`)}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 1, minHeight: 56 }}>
                          <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: '1rem', color: CK.navy, wordBreak: 'break-word', minWidth: 0 }}>
                            {standardLabel(std.standardId)}
                          </Typography>
                          {noData && <Chip size="small" label="No data yet" sx={statusChipSx('neutral')} />}
                        </Box>
                        <Box sx={{ minHeight: 92 }}>
                          {noData ? (
                            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8rem', color: CK.muted, fontWeight: 500 }}>
                              Scores appear once AWS finishes evaluating this standard.
                            </Typography>
                          ) : (
                            <>
                              <Typography variant="caption" color="text.secondary">Security score</Typography>
                              <Typography sx={{ ...scoreSx(pct), fontFamily: FONT_BODY, fontSize: '2.5rem', lineHeight: 1, my: 0.5 }}>
                                {pct}%
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {std.passingCount} / {total} controls passing
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={pct}
                                color={barColor(pct)}
                                sx={{ height: 8, borderRadius: 1, mt: 1 }}
                              />
                            </>
                          )}
                        </Box>
                        <Box sx={statTileFooterSx}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/securityhub/cspm/standards/${encodeURIComponent(std.standardId)}`);
                            }}
                          >
                            View controls
                          </Button>
                        </Box>
                      </Paper>
                    </div>
                  </Grid>
                );
              })}
            </Grid>
        </motion.div>
      )}
    </motion.div>
  );
}
